import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { getListAsync, getMonthlyViewAsync } from "../../services/reducers/timeReducer";
import {
  convertMinutesToHoursAndMinutes,
  formatDate,
} from "../../utils/common";
import TimeTrackerMonthlyEmpty from "./TimeTrackerMonthlyEmpty";
import Pagination from "../admin/projects/Pagination";
import EchopulseLoader from "../loader/EchopulseLoader";

type Props = {
  startOfMonth: Date;
  endOfMonth: Date;
  activeTab: string;
};
const TimeTrackerMonthly: React.FC<Props> = ({
  startOfMonth,
  endOfMonth,
  activeTab,
}) => {
  const dispatch = useAppDispatch();
  const [list, setList] = useState<any>([]);
  const [taskList, setTaskList] = useState<any>([]);
  const commonMonths = new Set();
  const isFetching = useRef(false);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    if (activeTab === "tab3") {
      getList();
    }
  }, [startOfMonth, endOfMonth, activeTab]);

  const getList = () => {
    if (isFetching.current) return;

    isFetching.current = true;
    setLoading(true);
    const request: any = {
      StartDate: formatDate(startOfMonth),
      EndDate: formatDate(endOfMonth),
    };
    dispatch(getMonthlyViewAsync(request)).then((res) => {
      if (res.payload) {
        setList(res.payload.records);
        setTaskList(res.payload.records.flatMap((t: any) => t.monthly));
        isFetching.current = false;
        setLoading(false);
      }
    });
  };

  const getMonthAbbreviation = (monthValue: any) => {
    const monthAbbreviations = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const validMonthValue = Math.max(1, Math.min(12, monthValue));
    const monthAbbreviation = monthAbbreviations[validMonthValue - 1];
    return monthAbbreviation;
  };

  const filteredList = list?.filter((t: any) => t.summary !== "Total");

  const monthlyTotals = filteredList?.[0]?.monthly.map((m: any, index: number) => {
    const total = filteredList.reduce((sum: number, task: any) => {
      return sum + task.monthly[index].totalTimeSpent;
    }, 0);
    return {
      month: m.month,
      year: m.year,
      totalTimeSpent: total,
    };
  });

  return (
    <div className="border border-gray-100 shadow-300 bg-white rounded-md relative min-h-[8rem] h-[calc(100vh-11.1rem)] flex flex-col flex-grow overflow-auto" id="timeTrackerMonthly">
      <div className="flex-grow overflow-auto">
        <div className="h-[3.75rem] bg-gray-200 rounded-tr-md sticky left-0 top-0 z-50 ps-8 pe-6">
          <div className="flex justify-start gap-0 h-full">
            <div className="flex items-center justify-start w-[calc(100%-44rem)]">
              <div className="text-500 font-bold text-gray-300">Tasks</div>
            </div>
            <div className="flex justify-start text-center gap-[0.75rem] w-[37.5rem]">
              {taskList.map((task: any) => {
                const monthAbbreviation = getMonthAbbreviation(task.month);
                if (commonMonths.has(monthAbbreviation)) {
                  return null;
                }

                commonMonths.add(monthAbbreviation);

                return (
                  <div
                    // key={task.identifier}
                    className="flex justify-center items-center w-[5.625rem] h-full"
                  >
                    <div>
                      <div className="text-500 font-bold text-gray-300">
                        {monthAbbreviation}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end text-center gap-[0.75rem]">
              <div className="flex justify-end items-center w-[5.625rem] h-full">
                <div>
                  <div className="text-500 font-bold text-gray-300">Total</div>
                  <div className="text-400 font-normal text-gray-300">
                    Hours
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="h-[3.75rem] bg-white rounded-tr-md ps-8 pe-6 sticky top-[3.75rem] left-0 z-50">
          <div className="flex justify-start gap-0 border-b border-gray-400 h-full">
            <div className="flex items-center justify-start w-[calc(100%-44rem)] h-full">
              <div className="text-400 font-normal text-gray-300">
                Total hours
              </div>
            </div>
            <div className="flex justify-start text-center gap-[0.75rem] w-[37.5rem]">
              <div className="flex justify-start text-center gap-[0.75rem] w-[37.5rem]">
                {(monthlyTotals ?? Array(6).fill({ totalTimeSpent: 0 })).map((monthData: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex justify-center items-center w-[5.625rem] h-full"
                  >
                    <div className="text-400 font-bold text-gray-300">
                      {monthData.totalTimeSpent > 0
                        ? convertMinutesToHoursAndMinutes(monthData.totalTimeSpent)
                        : "00:00"}
                    </div>
                  </div>
                ))}
              </div>

            </div>
            <div className="flex justify-end items-center w-[5.625rem] h-full">
              <div className="text-400 font-bold text-primary-100">
                {list?.length > 0
                  ? convertMinutesToHoursAndMinutes(
                    list[0].monthly.reduce(
                      (sum: number, task: any) =>
                        sum + task.totalTimeSpent,
                      0
                    )
                  )
                  : "-"}
              </div>
            </div>
          </div>
        </div>
        {loading ? (
          <EchopulseLoader />
        ) :
          filteredList && filteredList.length > 0 ? (
            filteredList.map((t: any) => (
              <div className="h-[4.875rem] bg-white rounded-tr-md relative ps-8 pe-6 after:absolute after:h-9 after:w-[0.1875rem] after:bg-green-100 after:top-1/2 after:-translate-y-1/2 after:left-0 after:rounded-e-md">
                <div className="flex justify-start gap-0 border-b border-gray-400 h-full">
                  <div className="flex items-center justify-start w-[calc(100%-44rem)] h-full py-4">
                    <div className="w-full pe-1">
                      <div className="text-400 font-bold text-gray-300 flex items-center">
                        <span className="w-[calc(100%-28px)] line-clamp-1">
                          {t.summary}
                        </span>
                      </div>
                      <div className="text-200 font-normal text-gray-700 flex mt-1">
                        <span className="max-w-[70%] line-clamp-1">
                          {t.projectKey} -{" "}
                          {t.projectName}

                        </span>{" "}
                        <span className="ms-2.5 ps-2.5 border border-y-0 border-e-0 border-s-gray-700 line-clamp-1">
                          #{t.taskKey}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-start text-center gap-[0.75rem] py-4 w-[37.5rem]">
                    {t.monthly
                      .map((t1: any) => (
                        <div
                          key={`${t.identifier}-${t1.month}`}
                          className="flex justify-center items-center w-[5.625rem] h-full rounded-md bg-gray-200"
                        >
                          <div className="text-400 font-normal text-gray-300">
                            {t1.totalTimeSpent > 0
                              ? convertMinutesToHoursAndMinutes(t1.totalTimeSpent)
                              : "-"}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <TimeTrackerMonthlyEmpty />

          )}
      </div>
      <div className="bg-white py-3 px-6 rounded-b-md">
        {" "}
      </div>
    </div>
  );
};

export default TimeTrackerMonthly;
